import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { FilterSelect } from "../components/FilterSelect";
import CrmObjectsLive from "./CrmObjectsLive";
import type { CrmObject } from "../services/crmObjectsService";
import type { CrudColumn, CrudRow } from "../types";
import {
  fetchProductCatalog,
  filterCatalog,
  priceLabel,
  unreachablePlatforms,
  ORIGIN_META,
  AUDIENCE_META,
  originMeta,
  profitOf,
  type CatalogFilters,
  type CatalogProduct,
  type ProductAudience,
  type ProductCatalog,
  type ProductOrigin,
} from "../services/productCatalogService";

// ===========================================================================
// Productos — todo lo que se vende, en una sola lista:
//   1. los productos y servicios dados de alta en el CRM,
//   2. las licencias de la plataforma,
//   3. los productos importados de HubSpot,
//   4. los productos y servicios del Punto de venta de la plataforma.
// En CC-CONSOLE la lista trae además lo de sus otras plataformas (Wayak,
// Nuvex); en Wayak y Nuvex, solo lo propio. Ese alcance lo decide el servidor.
//
// «Catálogo» es de solo lectura. Los productos propios del CRM se dan de alta y
// se editan en «Administrar productos del CRM»; las licencias, en la
// configuración de licencias de la plataforma.
// ===========================================================================

type Tab = "catalogo" | "administrar";
const ORIGINS: ProductOrigin[] = ["crm", "license", "pos", "hubspot"];
const AUDIENCES: ProductAudience[] = ["public", "reseller"];
const NO_FILTERS: CatalogFilters = { query: "", origin: "all", platform: "all", audience: "all" };

const money = (value: unknown, currency = "MXN") =>
  value == null || value === ""
    ? "—"
    : new Intl.NumberFormat("es-MX", { style: "currency", currency }).format(Number(value));
const text = (value: unknown) => (value == null || value === "" ? "—" : String(value));

type Row = CrmObject & CrudRow;
const adminColumns: CrudColumn<Row>[] = [
  { key: "name", label: "Producto", render: (r) => text(r.name) },
  { key: "sku", label: "SKU", render: (r) => text(r.sku) },
  { key: "price", label: "Precio", align: "right", render: (r) => money(r.price, String(r.currency || "MXN")) },
  { key: "cost", label: "Costo", align: "right", render: (r) => money(r.cost, String(r.currency || "MXN")) },
];

function OriginChip({ origin }: { origin: ProductOrigin }) {
  const meta = originMeta(origin);
  return (
    <span
      data-testid={`origin-${origin}`}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.tone}`}
    >
      <i className={meta.icon} />
      {meta.label}
    </span>
  );
}

/** Para quién está disponible: el público o solo el admin reseller. */
function AudienceChip({ audience }: { audience?: ProductAudience | null }) {
  const meta = audience ? AUDIENCE_META[audience] : null;
  if (!meta) return <span className="text-[11px] text-gray-400">—</span>;
  return (
    <span
      data-testid={`audience-${audience}`}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.tone}`}
    >
      <i className={meta.icon} />
      {meta.label}
    </span>
  );
}

function Catalog() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const [catalog, setCatalog] = useState<ProductCatalog>({ rows: [], platforms: [], seesCost: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CatalogFilters>(NO_FILTERS);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCatalog(await fetchProductCatalog());
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudo cargar el catálogo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => filterCatalog(catalog.rows, filters), [catalog.rows, filters]);
  const platforms = useMemo(() => catalog.platforms.map((p) => p.product), [catalog.platforms]);
  const down = useMemo(() => unreachablePlatforms(catalog.platforms), [catalog.platforms]);
  const count = (pick: (r: CatalogProduct) => boolean) => catalog.rows.filter(pick).length;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <FilterSelect
          label="Origen"
          value={filters.origin}
          onChange={(v) => setFilters((f) => ({ ...f, origin: v as CatalogFilters["origin"] }))}
          allCount={catalog.rows.length}
          options={ORIGINS.map((o) => ({ value: o, label: ORIGIN_META[o].label, count: count((r) => r.origin === o) }))}
          isDarkMode={isDarkMode}
        />
        <FilterSelect
          label="Disponible para"
          value={filters.audience ?? "all"}
          onChange={(v) => setFilters((f) => ({ ...f, audience: v as CatalogFilters["audience"] }))}
          allCount={catalog.rows.length}
          options={AUDIENCES.map((a) => ({ value: a, label: AUDIENCE_META[a].label, count: count((r) => r.audience === a) }))}
          isDarkMode={isDarkMode}
        />
        {/* Con una sola plataforma (Wayak, Nuvex) el filtro no aporta nada. */}
        {platforms.length > 1 && (
          <FilterSelect
            label="Plataforma"
            value={filters.platform}
            onChange={(v) => setFilters((f) => ({ ...f, platform: v }))}
            allLabel="Todas"
            allCount={catalog.rows.length}
            options={platforms.map((p) => ({ value: p, label: p, count: count((r) => r.platform === p) }))}
            isDarkMode={isDarkMode}
          />
        )}
        <div className={`ml-auto flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
          <i className={`ri-search-line ${tc.textSecondary}`} />
          <input
            value={filters.query}
            onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
            placeholder="Buscar producto o SKU…"
            className={`bg-transparent text-sm outline-none ${tc.text}`}
          />
        </div>
        <button onClick={load} title="Actualizar" className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.buttonHover}`}>
          <i className={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} />
        </button>
      </div>

      {down.length > 0 && (
        <p data-testid="catalog-unreachable" className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          <i className="ri-error-warning-line mr-1" />
          No se pudo leer todo lo de {down.join(", ")}: la lista puede estar incompleta para {down.length === 1 ? "esa plataforma" : "esas plataformas"}.
        </p>
      )}

      <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
        {loading ? (
          <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
            <i className="ri-loader-4-line mr-2 animate-spin" />
            Cargando catálogo...
          </div>
        ) : error ? (
          <div className="p-10 text-center">
            <p className="text-sm text-red-500">{error}</p>
            <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-white hover:bg-primary-400">
              Reintentar
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
            <i className="ri-price-tag-3-line mb-2 block text-3xl" />
            {catalog.rows.length === 0 ? "Aún no hay productos ni licencias." : "Nada coincide con los filtros."}
          </div>
        ) : (
          <div className="w-full overflow-x-auto p-4">
            <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
              <thead className="bg-primary-500 text-white">
                <tr>
                  <th className="p-3 text-left font-bold">Producto</th>
                  <th className="p-3 text-center font-bold">Origen</th>
                  <th className="p-3 text-center font-bold">Disponible para</th>
                  {platforms.length > 1 && <th className="p-3 text-left font-bold">Plataforma</th>}
                  <th className="p-3 text-left font-bold">SKU</th>
                  <th className="p-3 text-right font-bold">{catalog.seesCost ? "Precio de venta" : "Precio"}</th>
                  {catalog.seesCost && <th className="p-3 text-right font-bold">Precio de compra</th>}
                  {catalog.seesCost && <th className="p-3 text-right font-bold">Utilidad</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.key}
                    className={
                      isDarkMode
                        ? i % 2 === 0 ? "bg-[#232323] text-white" : "bg-[#1C1C1C] text-white"
                        : i % 2 === 0 ? "bg-gray-50 text-gray-900" : "bg-white text-gray-900"
                    }
                  >
                    <td className="p-2 font-medium">
                      {r.image && (
                        <img src={r.image} alt="" loading="lazy" className="mr-2 inline-block h-8 w-8 rounded-md bg-white object-contain align-middle" />
                      )}
                      {text(r.name)}
                      {r.status === "draft" && <span className={`ml-2 text-[10px] ${tc.textSecondary}`}>(borrador)</span>}
                    </td>
                    <td className="p-2 text-center"><OriginChip origin={r.origin} /></td>
                    <td className="p-2 text-center"><AudienceChip audience={r.audience} /></td>
                    {platforms.length > 1 && <td className="p-2">{r.platform}</td>}
                    <td className="p-2">{text(r.sku)}</td>
                    <td className="p-2 text-right whitespace-nowrap">{priceLabel(r)}</td>
                    {catalog.seesCost && <td className="p-2 text-right">{money(r.cost, r.currency)}</td>}
                    {catalog.seesCost && (
                      <td className={`p-2 text-right whitespace-nowrap font-medium ${(profitOf(r)?.amount ?? 0) < 0 ? "text-red-500" : profitOf(r) ? "text-emerald-600" : ""}`}>
                        {profitOf(r)
                          ? `${money(profitOf(r)!.amount, r.currency)}${profitOf(r)!.margin === null ? "" : ` · ${profitOf(r)!.margin!.toFixed(1)} %`}`
                          : r.cost != null && !r.price
                            ? "Por cotizar"
                            : "—"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className={`mt-3 text-xs ${tc.textSecondary}`}>
        <i className="ri-information-line mr-1" />
        {rows.length} de {catalog.rows.length} · Las licencias se administran en la configuración de licencias de la
        plataforma; los productos del CRM, en «Administrar productos del CRM».
      </p>
    </div>
  );
}

export default function ProductosSection() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const [tab, setTab] = useState<Tab>("catalogo");
  const tabClass = (active: boolean) =>
    `rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
      active ? "bg-primary-500 text-white" : `border ${tc.border} ${tc.text} hover:border-primary-500`
    }`;

  return (
    <div className="flex h-full w-full flex-col">
      <div className="px-6 pt-6">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-price-tag-3-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Productos</h1>
              <p className={`text-sm ${tc.textSecondary}`}>Productos y servicios del CRM y del Punto de venta, licencias de la plataforma e importados de HubSpot</p>
            </div>
          </div>
          <div className="flex items-center gap-2" role="tablist">
            <button role="tab" aria-selected={tab === "catalogo"} onClick={() => setTab("catalogo")} className={tabClass(tab === "catalogo")}>
              Catálogo
            </button>
            <button role="tab" aria-selected={tab === "administrar"} onClick={() => setTab("administrar")} className={tabClass(tab === "administrar")}>
              Administrar productos del CRM
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === "catalogo" ? (
          <div className="p-6">
            <Catalog />
          </div>
        ) : (
          <CrmObjectsLive
            type="products"
            title="Productos del CRM"
            subtitle="Alta y edición; se comparten con HubSpot"
            icon="ri-price-tag-3-line"
            columns={adminColumns}
          />
        )}
      </div>
    </div>
  );
}
